// Framework-free local-mutation store.
//
// This owns the app's flashcard / bookmark / history state and ALL of the sync
// bookkeeping that goes with a mutation (dirty flags, tombstones, collection LMs,
// the name→id map). It is deliberately free of React so that BOTH the app
// (AppContext.js, a thin React shell over this) and the headless multi-device test
// client (syncTestKit.createClient) drive the exact same mutation logic — closing
// the fidelity gap where the test client used to re-implement these mutations and
// could pass while the real app lost data.
//
// It also owns the two things a naive caller gets wrong and loses data on:
//   • a mutation made WHILE a sync was in flight must survive it: the engine folds it
//     into the result it returns, which applyFlashcards/applyBookmarks/applyHistory
//     install as-is (see B1).
//   • the engine hand-off getters read the LIVE canonical state at call time.
//
// `now` is injected so each simulated device can run its own clock (real per-device
// skew) while production passes Date.now.

const MAX_HISTORY = 1000;

export const createAppStore = ({ store, now = Date.now, initial = {} }) => {
  let collections = initial.collections ? { ...initial.collections } : {};
  let bookmarks = initial.bookmarks ? { ...initial.bookmarks } : {};
  let history = initial.history ? [...initial.history] : [];

  const cid = (name) => store.getOrCreateCollectionId(name);
  const bumpCollection = (name, ts = now()) => {
    const id = cid(name);
    store.setCollectionLM(id, ts);
    store.markCollectionDirty(id);
    return id;
  };
  const makeCard = (word, form, definition, root, wordID, ts = now()) => ({
    word: form ? `${form} - ${word}` : word,
    wordID,
    root,
    definition,
    addedAt: ts,
    lastModified: ts,
  });
  // Own-property lookup: a name like "constructor" must not resolve to an
  // Object.prototype member (that threw / blanked the app).
  const getCollection = (name) =>
    Object.prototype.hasOwnProperty.call(collections, name) ? collections[name] : undefined;

  // ── Flashcards ────────────────────────────────────────────────────────────
  const addCollection = (name, ts = now()) => {
    if (getCollection(name)) return collections;
    bumpCollection(name, ts);
    collections = { ...collections, [name]: [] };
    return collections;
  };

  const addFlashcard = (word, form, definition, root, wordID, name, ts = now()) => {
    const existing = getCollection(name) || [];
    if (existing.some((f) => f.wordID === wordID)) return collections;
    bumpCollection(name, ts); // only bump on the path that actually inserts
    collections = { ...collections, [name]: [...existing, makeCard(word, form, definition, root, wordID, ts)] };
    return collections;
  };

  // Immutable, positional edit that bumps lastModified so the edit propagates.
  const editFlashcard = (name, index, patch, ts = now()) => {
    const list = getCollection(name) || [];
    const card = list[index];
    if (!card) return collections;
    const changed = Object.keys(patch).some((k) => card[k] !== patch[k]);
    if (!changed) return collections;
    const id = cid(name);
    store.setCollectionLM(id, ts);
    store.markCollectionDirty(id);
    collections = {
      ...collections,
      [name]: list.map((c, i) => (i === index ? { ...c, ...patch, lastModified: ts } : c)),
    };
    return collections;
  };

  const removeFlashcard = (wordID, name, ts = now()) => {
    const list = getCollection(name);
    if (!list || !list.some((f) => f.wordID === wordID)) return collections;
    const id = cid(name);
    store.addFlashcardTombstone(id, wordID, ts);
    store.setCollectionLM(id, ts);
    store.markCollectionDirty(id);
    collections = { ...collections, [name]: list.filter((f) => f.wordID !== wordID) };
    return collections;
  };

  // Undo of removeFlashcard: put the removed card back at its old position with its
  // original fields (addedAt included) instead of appending a freshly stamped copy.
  // Only lastModified moves on, past the delete, so the card also beats a deletion
  // that already reached Drive; the local tombstone is dropped.
  const restoreFlashcard = (card, name, index, ts = now()) => {
    const list = getCollection(name) || [];
    if (!card || list.some((f) => f.wordID === card.wordID)) return collections;
    const id = bumpCollection(name, ts);
    const tombstones = store.getFlashcardTombstones();
    if (tombstones[id] && Object.prototype.hasOwnProperty.call(tombstones[id], card.wordID)) {
      const bucket = { ...tombstones[id] };
      delete bucket[card.wordID];
      store.setFlashcardTombstonesAll({ ...tombstones, [id]: bucket });
    }
    const at = Math.min(Math.max(0, Number(index) || 0), list.length);
    collections = {
      ...collections,
      [name]: [...list.slice(0, at), { ...card, lastModified: ts }, ...list.slice(at)],
    };
    return collections;
  };

  const moveFlashcard = (wordID, from, to, ts = now()) => {
    const card = (getCollection(from) || []).find((f) => f.wordID === wordID);
    if (!card) return collections;
    const fromId = cid(from);
    const toId = cid(to);
    // Collection-scoped delete in the source so the move is remove-here + add-there
    // (no cross-device duplicate, no over-delete of the same word elsewhere).
    store.addFlashcardTombstone(fromId, wordID, ts);
    store.setCollectionLM(fromId, ts);
    store.setCollectionLM(toId, ts);
    store.markCollectionDirty(fromId);
    store.markCollectionDirty(toId);
    const moved = { ...card, lastModified: ts };
    const newFrom = collections[from].filter((f) => f.wordID !== wordID);
    const toCards = getCollection(to) || [];
    const newTo = toCards.some((f) => f.wordID === wordID) ? toCards : [...toCards, moved];
    collections = { ...collections, [from]: newFrom, [to]: newTo };
    return collections;
  };

  const renameCollection = (oldName, newName, ts = now()) => {
    if (!Object.prototype.hasOwnProperty.call(collections, oldName)) return collections;
    // Renaming onto itself would write next[newName] and then delete it (losing the
    // whole collection); renaming onto another collection would overwrite it.
    if (oldName === newName || Object.prototype.hasOwnProperty.call(collections, newName)) return collections;
    store.renameCollectionId(oldName, newName);
    const id = cid(newName);
    store.setCollectionLM(id, ts);
    store.markCollectionDirty(id);
    // Built as entries, not by assignment: next["__proto__"] = … would replace the
    // object's prototype instead of adding a collection. Renamed goes last, as before.
    collections = Object.fromEntries([
      ...Object.entries(collections).filter(([name]) => name !== oldName),
      [newName, (collections[oldName] || []).map((c) => ({ ...c, lastModified: ts }))],
    ]);
    return collections;
  };

  const deleteCollection = (name, ts = now()) => {
    if (!Object.prototype.hasOwnProperty.call(collections, name)) return collections;
    const id = cid(name);
    store.addCollectionTombstone(id, ts);
    store.deleteCollectionId(name);
    store.clearCollectionDirty(id);
    const next = { ...collections };
    delete next[name];
    collections = next;
    return collections;
  };

  // REPLACE local collections with the imported set, stamping + marking each dirty.
  // Non-imported collections are pulled back from Drive by the engine (never
  // emptied — the engine no longer trusts a lingering id-map entry), so import is
  // additive against Drive rather than destructive.
  const importJSON = (map, ts = now()) => {
    // Built as entries (see renameCollection): a file's "__proto__" collection would
    // otherwise be dropped, leaving only its orphaned id behind.
    collections = Object.fromEntries(
      Object.entries(map || {}).map(([name, cards]) => {
        const id = cid(name);
        store.setCollectionLM(id, ts);
        store.markCollectionDirty(id);
        const stamped = (cards || []).map((c) => ({
          ...c,
          addedAt: c.addedAt ?? ts,
          lastModified: c.lastModified ?? ts,
        }));
        return [name, stamped];
      })
    );
    return collections;
  };

  // ── Bookmarks ─────────────────────────────────────────────────────────────
  const addBookmark = (word, definitions, ts = now()) => {
    store.setBookmarkMeta(word, { addedAt: ts, lastModified: ts });
    bookmarks = { ...bookmarks, [word]: definitions };
    return bookmarks;
  };
  const removeBookmark = (word, ts = now()) => {
    store.addBookmarkTombstone(word, ts);
    store.deleteBookmarkMeta(word);
    const next = { ...bookmarks };
    delete next[word];
    bookmarks = next;
    return bookmarks;
  };
  const clearBookmarks = (ts = now()) => {
    for (const word of Object.keys(bookmarks)) {
      store.addBookmarkTombstone(word, ts);
      store.deleteBookmarkMeta(word);
    }
    bookmarks = {};
    return bookmarks;
  };

  // ── History ───────────────────────────────────────────────────────────────
  const addToHistory = (entry, ts = now()) => {
    const e = typeof entry === "string" ? { word: entry, timestamp: ts } : { timestamp: ts, ...entry };
    const word = e.word;
    history = [e, ...history.filter((h) => (typeof h === "string" ? h : h.word) !== word)].slice(0, MAX_HISTORY);
    return history;
  };
  const removeHistoryItem = (word, ts = now()) => {
    store.addHistoryTombstone(word, ts);
    history = history.filter((h) => (typeof h === "string" ? h : h.word) !== word);
    return history;
  };
  const clearHistory = (ts = now()) => {
    for (const h of history) store.addHistoryTombstone(typeof h === "string" ? h : h.word, ts);
    history = [];
    return history;
  };

  // ── Engine hand-off (read the LIVE canonical state at call time) ──────────
  const buildFlashcardLocal = () => ({
    collections,
    idMap: store.getCollectionIdMap(),
    fcTombstones: store.getFlashcardTombstones(),
    colTombstones: store.getCollectionTombstones(),
    collectionMeta: store.getCollectionMeta(),
  });
  const buildBookmarkLocal = () => ({
    items: bookmarks,
    meta: store.getBookmarksMeta(),
    tombstones: store.getBookmarkTombstones(),
  });
  const buildHistoryLocal = () => history;

  // ── Apply a sync result ───────────────────────────────────────────────────
  // The engine folds a change made while the sync was in flight into the result it
  // returns, matched by collection id, so the result is installed as-is. Keeping a
  // local copy by name instead could file another collection's cards under it: by
  // now the name may belong to a different collection (B1).
  const applyFlashcards = (merged) => {
    collections = merged.collections || {};
    return collections;
  };
  // Simple clobber: the engine already folds any mid-sync mutation into the result
  // it returns (with consistent meta), so the returned state is authoritative.
  const applyBookmarks = (merged) => {
    bookmarks = merged.items || {};
    return bookmarks;
  };
  const applyHistory = (mergedEntries) => {
    history = mergedEntries || [];
    return history;
  };

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  // Wipe local state + all sync metadata (logout). The CALLER is responsible for
  // flushing pending syncs BEFORE calling this (see B5) — reset itself is the
  // final, unconditional local wipe.
  const reset = () => {
    collections = {};
    bookmarks = {};
    history = [];
    store.clearAllSyncState();
  };

  return {
    getCollections: () => collections,
    getBookmarks: () => bookmarks,
    getHistory: () => history,
    // direct setters (used by React shell to install merged state / restore)
    setCollections: (c) => { collections = c || {}; return collections; },
    setBookmarks: (b) => { bookmarks = b || {}; return bookmarks; },
    setHistory: (h) => { history = h || []; return history; },
    addCollection, addFlashcard, editFlashcard, removeFlashcard, restoreFlashcard, moveFlashcard,
    renameCollection, deleteCollection, importJSON, makeCard,
    addBookmark, removeBookmark, clearBookmarks,
    addToHistory, removeHistoryItem, clearHistory,
    buildFlashcardLocal, buildBookmarkLocal, buildHistoryLocal,
    applyFlashcards, applyBookmarks, applyHistory,
    reset,
  };
};
